import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, ToggleLeft, ToggleRight,
  Tag, Loader2, Check, X, Calendar, Users,
} from "lucide-react";

const PLAN_LABELS = { monthly: "Monthly", yearly: "Yearly" };

function Badge({ children, color }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider border font-bold ${color}`}>
      {children}
    </span>
  );
}

export default function AdminPromoCodes() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const [form, setForm] = useState({
    code: "",
    discount_percent: "",
    applicable_plans: ["monthly", "yearly"],
    max_uses: "",
    expires_at: "",
  });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/promo-codes");
      setCodes(data.codes || []);
    } catch { toast.error("Could not load promo codes"); }
    finally { setLoading(false); }
  };

  const togglePlan = (plan) => {
    setForm((f) => ({
      ...f,
      applicable_plans: f.applicable_plans.includes(plan)
        ? f.applicable_plans.filter((p) => p !== plan)
        : [...f.applicable_plans, plan],
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) { toast.error("Code is required"); return; }
    const pct = parseInt(form.discount_percent, 10);
    if (!pct || pct < 1 || pct > 100) { toast.error("Discount must be 1–100%"); return; }
    if (form.applicable_plans.length === 0) { toast.error("Select at least one plan"); return; }
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        discount_percent: pct,
        applicable_plans: form.applicable_plans,
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      };
      const { data } = await api.post("/admin/promo-codes", payload);
      setCodes((prev) => [data, ...prev]);
      setForm({ code: "", discount_percent: "", applicable_plans: ["monthly", "yearly"], max_uses: "", expires_at: "" });
      setShowForm(false);
      toast.success(`Promo code ${data.code} created`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create promo code");
    } finally { setSaving(false); }
  };

  const handleToggle = async (c) => {
    setTogglingId(c.code_id);
    try {
      const { data } = await api.patch(`/admin/promo-codes/${c.code_id}`, { active: !c.active });
      setCodes((prev) => prev.map((p) => p.code_id === c.code_id ? { ...p, active: data.active } : p));
      toast.success(`${c.code} ${data.active ? "activated" : "deactivated"}`);
    } catch { toast.error("Could not update promo code"); }
    finally { setTogglingId(null); }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete promo code ${c.code}? This cannot be undone.`)) return;
    setDeletingId(c.code_id);
    try {
      await api.delete(`/admin/promo-codes/${c.code_id}`);
      setCodes((prev) => prev.filter((p) => p.code_id !== c.code_id));
      toast.success(`${c.code} deleted`);
    } catch { toast.error("Could not delete promo code"); }
    finally { setDeletingId(null); }
  };

  const isExpired = (c) => c.expires_at && new Date(c.expires_at) < new Date();
  const isExhausted = (c) => c.max_uses != null && c.times_used >= c.max_uses;

  return (
    <main className="max-w-5xl mx-auto px-6 md:px-12 py-12">
      <Link to="/admin" className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-[#0025D8] inline-flex items-center gap-1.5 mb-4 transition-colors group">
        <ArrowLeft className="w-3 h-3 group-hover:-translate-x-0.5 transition-transform" /> Admin
      </Link>

      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#0025D8]">Admin · Marketing</div>
          <h1 className="font-display font-black text-4xl tracking-tight text-[#0F172A] mt-2">Promo Codes</h1>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#0025D8] hover:bg-[#001CB3] text-white text-sm font-semibold transition-colors"
        >
          {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showForm ? "Cancel" : "New Promo Code"}
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <form onSubmit={handleCreate} className="rounded-xl border border-[#0025D8]/30 bg-[#0025D8]/3 p-6 mb-8 space-y-5">
          <div className="font-mono text-[10px] tracking-[0.2em] text-[#0025D8] uppercase mb-1">New promo code</div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">Code <span className="text-[#E11D48]">*</span></label>
              <input
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="e.g. LAUNCH20"
                className="w-full rounded-xl border border-border bg-white px-4 py-2.5 text-sm font-mono uppercase focus:border-[#0025D8] focus:ring-2 focus:ring-[#0025D8]/15 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">Discount % <span className="text-[#E11D48]">*</span></label>
              <input
                type="number" min="1" max="100"
                value={form.discount_percent}
                onChange={(e) => setForm((f) => ({ ...f, discount_percent: e.target.value }))}
                placeholder="e.g. 20"
                className="w-full rounded-xl border border-border bg-white px-4 py-2.5 text-sm focus:border-[#0025D8] focus:ring-2 focus:ring-[#0025D8]/15 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">
                Max uses <span className="font-normal text-muted-foreground/60">optional — blank = unlimited</span>
              </label>
              <input
                type="number" min="1"
                value={form.max_uses}
                onChange={(e) => setForm((f) => ({ ...f, max_uses: e.target.value }))}
                placeholder="e.g. 100"
                className="w-full rounded-xl border border-border bg-white px-4 py-2.5 text-sm focus:border-[#0025D8] focus:ring-2 focus:ring-[#0025D8]/15 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">
                Expires <span className="font-normal text-muted-foreground/60">optional</span>
              </label>
              <input
                type="date"
                value={form.expires_at}
                onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
                className="w-full rounded-xl border border-border bg-white px-4 py-2.5 text-sm focus:border-[#0025D8] focus:ring-2 focus:ring-[#0025D8]/15 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#0F172A] mb-2">Applicable plans <span className="text-[#E11D48]">*</span></label>
            <div className="flex gap-2">
              {["monthly", "yearly"].map((plan) => (
                <button
                  key={plan} type="button"
                  onClick={() => togglePlan(plan)}
                  className={`flex items-center gap-1.5 px-4 py-2 border text-sm font-medium transition-colors ${
                    form.applicable_plans.includes(plan)
                      ? "bg-[#0025D8] text-white border-[#0025D8]"
                      : "border-border text-muted-foreground hover:bg-secondary"
                  }`}
                >
                  {form.applicable_plans.includes(plan) && <Check className="w-3.5 h-3.5" />}
                  {PLAN_LABELS[plan]}
                </button>
              ))}
            </div>
          </div>

          <button
            type="submit" disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#0025D8] hover:bg-[#001CB3] text-white text-sm font-semibold transition-colors disabled:opacity-60"
          >
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : <><Plus className="w-4 h-4" /> Create code</>}
          </button>
        </form>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : codes.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-12 text-center">
          <Tag className="w-8 h-8 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No promo codes yet. Create one above.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary border-b border-border">
              <tr>
                {["Code", "Discount", "Plans", "Usage", "Expires", "Status", ""].map((h) => (
                  <th key={h} className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {codes.map((c) => {
                const expired = isExpired(c);
                const exhausted = isExhausted(c);
                const effectivelyInactive = !c.active || expired || exhausted;
                return (
                  <tr key={c.code_id} className="hover:bg-secondary/40 transition-colors">
                    <td className="px-4 py-3">
                      <span className="font-mono font-bold text-[#0025D8] text-sm">{c.code}</span>
                    </td>
                    <td className="px-4 py-3 font-semibold">{c.discount_percent}%</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 flex-wrap">
                        {(c.applicable_plans || []).map((p) => (
                          <Badge key={p} color="bg-[#0025D8]/8 text-[#0025D8] border-[#0025D8]/20">{p}</Badge>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Users className="w-3 h-3" />
                        {c.times_used}
                        {c.max_uses != null && <span>/ {c.max_uses}</span>}
                        {exhausted && <Badge color="bg-amber-50 text-amber-700 border-amber-300">Full</Badge>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {c.expires_at ? (
                        <div className={`flex items-center gap-1 text-xs ${expired ? "text-[#E11D48]" : "text-muted-foreground"}`}>
                          <Calendar className="w-3 h-3" />
                          {new Date(c.expires_at).toLocaleDateString("en", { month: "short", day: "numeric", year: "numeric" })}
                          {expired && <Badge color="bg-[#E11D48]/10 text-[#E11D48] border-[#E11D48]/30">Expired</Badge>}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">No expiry</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {effectivelyInactive
                        ? <Badge color="bg-secondary text-muted-foreground border-border">Inactive</Badge>
                        : <Badge color="bg-[#10B981]/10 text-[#047857] border-[#10B981]/30">Active</Badge>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={() => handleToggle(c)}
                          disabled={togglingId === c.code_id || expired || exhausted}
                          title={c.active ? "Deactivate" : "Activate"}
                          className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-[#0025D8] disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {togglingId === c.code_id
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : c.active ? <ToggleRight className="w-4 h-4 text-[#10B981]" /> : <ToggleLeft className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleDelete(c)}
                          disabled={deletingId === c.code_id}
                          title="Delete"
                          className="p-1.5 rounded-lg hover:bg-[#E11D48]/10 transition-colors text-muted-foreground hover:text-[#E11D48] disabled:opacity-40"
                        >
                          {deletingId === c.code_id
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <Trash2 className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
